/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { v4 as uuidv4 } from "uuid";
import { useAppContext } from "../Components/AppContext";
import ChangerTrackerNavigateOptions from "../Components/AppContext/ChangeTrackerNavigateOptions";
import IChangeTracker from "../Components/AppContext/IChangeTracker";

export type ResetChangeTracker<T> = (state: T) => void;

function deepEquals(a: any, b: any, ignoreKeys: PropertyKey[] = []): boolean {
    if (a == null || b == null) return Object.is(a, b)
    if (typeof a === "object") {
        if (Array.isArray(a)) {
            if (!Array.isArray(b)) return false;
            if (a.length !== b.length) return false;
            for (let i = 0; i < a.length; i++) {
                if (!deepEquals(a[i], b[i], ignoreKeys)) return false
            }

            return true;
        }

        for (let key of Object.keys(a)) {
            if (ignoreKeys.includes(key)) continue;
            if (!Object.hasOwn(b, key)) return false;
            if (!deepEquals(a[key], b[key], ignoreKeys)) return false;
        }

        return true;
    }

    return Object.is(a, b);
}

export default function useChangeTracker<T extends {}>(
    state: T | undefined | null,
    onSpeichern?: (options: ChangerTrackerNavigateOptions) => Promise<void>,
    ignore?: (keyof T)[]
): [IChangeTracker & { initialState: T | undefined | null }, ResetChangeTracker<T>] {
    const [initialState, setInitialState] = React.useState(state);
    const id = React.useRef(uuidv4())
    const { changeTracker: appChangeTracker } = useAppContext();

    const changeTracker = {
        hasAnyChanges() {
            return !deepEquals(initialState, state, ignore);
        },

        hasChanges(propertyName: string) {
            if (!state || !initialState) {
                return false;
            }

            const initialValue = initialState?.[propertyName as keyof T];
            const currentValue = state[propertyName as keyof T];
            return !deepEquals(initialValue, currentValue, ignore);
        },

        async speichere(options: ChangerTrackerNavigateOptions) {
            if (onSpeichern !== undefined) {
                await onSpeichern(options);
            }
        },

        get id() {
            return id.current;
        },

        initialState,
    };

    function resetChangeTracker(state: T) {
        setInitialState(state);
    }

    React.useEffect(() => {
        const unregister = appChangeTracker.register(changeTracker);
        return () => {
            unregister();
        }
    }, [appChangeTracker, changeTracker]);

    return [changeTracker, resetChangeTracker];
}
